import { useCallback, useEffect } from 'react';
import { parse, format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { getEndOfDay } from '../../utilities';
import { DatepickerComponent } from './Component';
import type { DatepickerComponentProps } from './Component';

export type DatepickerProps = Omit<
  DatepickerComponentProps,
  'value' | 'onChange'
> & {
  value?: string;
  onChange?: (newValue: string) => void;
  valueFormat?: string;

  disabled?: boolean;
  endOfDay?: boolean;
  cleanOnDisable?: boolean;
  useUtc?: boolean;
};

// example: '2024-12-24T20:14:17.111Z'
export const ISO_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";

const parseValue = (
  value: string,
  valueFormat: string,
  useUtc: boolean
): Date => {
  if (useUtc) {
    return parseISO(value);
  }

  return parse(value, valueFormat, new Date());
};

const formatValue = (
  date: Date,
  valueFormat: string,
  useUtc: boolean
): string => {
  if (useUtc) {
    return formatInTimeZone(date, 'UTC', valueFormat);
  }

  return format(date, valueFormat);
};

export const Datepicker: React.FC<DatepickerProps> = ({
  value,
  onChange,
  valueFormat = ISO_FORMAT,
  disabled,
  cleanOnDisable = false,
  endOfDay = false,
  useUtc = false,
  ...rest
}) => {
  const currentValue = value
    ? parseValue(value, valueFormat, useUtc)
    : undefined;

  const handleChange = useCallback(
    (newValue: Date | null) => {
      if (!newValue) {
        onChange?.('');
        return;
      }

      const formattedValue = formatValue(newValue, valueFormat, useUtc);
      const result = endOfDay ? getEndOfDay(formattedValue) : formattedValue;

      onChange?.(result);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valueFormat, endOfDay, useUtc]
  );

  useEffect(
    () => {
      if (!cleanOnDisable || !disabled) return;

      onChange?.('');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cleanOnDisable, disabled]
  );

  return (
    <DatepickerComponent
      disabled={disabled}
      {...rest}
      value={currentValue}
      onChange={handleChange}
    />
  );
};
